import { X } from 'lucide-react';
import { useEffect, useRef, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import styles from './Modal.module.css';
import { useArrastarDialog } from './useArrastarDialog';

export type ModalProps = {
  open: boolean;
  title: string;
  headerContent?: ReactNode;
  headerVariant?: 'default' | 'highlight' | 'dark';
  headerColor?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'md' | 'lg';
  /** Classe extra no miolo rolável do diálogo. */
  bodyClassName?: string;
  /** Quando informado, recebe o foco ao abrir em vez do container do dialog. */
  initialFocusRef?: RefObject<HTMLElement | null>;
};

const pilhaModais: symbol[] = [];

export function Modal({
  open,
  title,
  headerContent,
  headerVariant = 'default',
  headerColor,
  onClose,
  children,
  footer,
  size = 'md',
  bodyClassName,
  initialFocusRef,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const alcaRef = useRef<HTMLElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const { deslocamento, arrastando, onAlcaPointerDown, onOverlayClick } = useArrastarDialog({
    open,
    dialogRef,
    alcaRef,
  });

  // Foco e listeners so quando abre/fecha — nao a cada nova identidade de onClose
  // (ex.: digitacao no formulario recria o callback e roubava o foco do editor).
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    (initialFocusRef?.current ?? dialogRef.current)?.focus();
    document.body.style.overflow = 'hidden';

    const id = Symbol('modal');
    pilhaModais.push(id);
    const onKey = (e: KeyboardEvent) => {
      // Modal aninhado (ex.: confirmação dentro de assistente): só o do topo fecha.
      if (e.key === 'Escape' && pilhaModais[pilhaModais.length - 1] === id) onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      const idx = pilhaModais.indexOf(id);
      if (idx >= 0) pilhaModais.splice(idx, 1);
      if (pilhaModais.length === 0) document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
      prev?.focus();
    };
  }, [open, initialFocusRef]);

  if (!open) return null;

  const headerClass = [
    styles.header,
    headerVariant === 'highlight' ? styles.headerHighlight : '',
    headerVariant === 'dark' ? styles.headerDark : '',
    headerColor ? styles.headerCustom : '',
    arrastando ? styles.headerArrastando : '',
  ]
    .filter(Boolean)
    .join(' ');

  return createPortal(
    <div
      className={styles.overlay}
      onClick={() => onOverlayClick(() => onCloseRef.current())}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className={`${styles.dialog} ${size === 'lg' ? styles.dialogLg : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
        style={{ transform: `translate(${deslocamento.x}px, ${deslocamento.y}px)` }}
        onClick={(e) => e.stopPropagation()}
      >
        <header
          ref={alcaRef}
          className={headerClass}
          style={headerColor ? ({ '--header-color': headerColor } as CSSProperties) : undefined}
          onPointerDown={onAlcaPointerDown}
        >
          {headerContent ?? (
            <h2 id="modal-title" className={styles.title}>
              {title}
            </h2>
          )}
          <button type="button" className={styles.close} onClick={() => onCloseRef.current()} aria-label="Fechar">
            <X size={20} />
          </button>
        </header>
        <div className={[styles.body, bodyClassName].filter(Boolean).join(' ')}>{children}</div>
        {footer ? <footer className={styles.footer}>{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
